import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CreditCard, Plus, Trash2, Save, Clock, Users, UserPlus, Shield } from 'lucide-react';

const DURATION_LABELS = {
  monthly: 'Mensile',
  annual: 'Annuale',
  biennial: 'Biennale'
};

const DEFAULT_DURATIONS = {
  monthly: { enabled: false, price: 0 },
  annual: { enabled: true, price: 0 },
  biennial: { enabled: false, price: 0 }
};

const ROLE_LABELS = {
  user: 'Utente',
  integrator: 'Integratore'
};

export function SubscriptionPlansCard({ config, setConfig, onSave, saving, readOnly = false }) {
  
  const updatePlan = (index, field, value) => {
    if (readOnly) return;
    const updated = [...config.plans];
    if (field === 'max_links' || field === 'max_tickets' || field === 'max_users') {
      updated[index][field] = value === '' ? null : parseInt(value) || 0;
    } else if (field === 'features') {
      updated[index][field] = value.split('\n').filter(f => f.trim());
    } else if (field === 'promotes_to_integrator') {
      updated[index][field] = value;
    } else {
      updated[index][field] = value;
    }
    setConfig({ ...config, plans: updated });
  };

  const updateDuration = (planIndex, durationId, field, value) => {
    if (readOnly) return;
    const updated = [...config.plans];
    if (!updated[planIndex].durations) {
      updated[planIndex].durations = { ...DEFAULT_DURATIONS };
    }
    if (!updated[planIndex].durations[durationId]) {
      updated[planIndex].durations[durationId] = { enabled: false, price: 0 };
    }
    
    if (field === 'enabled') {
      updated[planIndex].durations[durationId].enabled = value;
    } else if (field === 'price') {
      updated[planIndex].durations[durationId].price = parseFloat(value) || 0;
    }
    
    setConfig({ ...config, plans: updated });
  };

  const updateAvailableFor = (planIndex, role, checked) => {
    if (readOnly) return;
    const updated = [...config.plans];
    const currentRoles = updated[planIndex].available_for || ['user', 'integrator'];
    
    if (checked) {
      if (!currentRoles.includes(role)) {
        updated[planIndex].available_for = [...currentRoles, role];
      }
    } else {
      updated[planIndex].available_for = currentRoles.filter(r => r !== role);
    }
    
    setConfig({ ...config, plans: updated });
  };

  const addPlan = () => {
    if (readOnly) return;
    setConfig({
      ...config,
      plans: [...config.plans, {
        id: `plan_${Date.now()}`,
        name: 'Nuovo Piano',
        max_links: 10,
        max_tickets: 3,
        max_users: null,
        promotes_to_integrator: false,
        features: ['Funzionalità base'],
        available_for: ['user', 'integrator'],
        durations: { ...DEFAULT_DURATIONS }
      }]
    });
  };

  const removePlan = (index) => {
    if (readOnly) return;
    if (config.plans.length <= 1) {
      toast.error('Devi avere almeno un piano');
      return;
    }
    const updated = config.plans.filter((_, i) => i !== index);
    setConfig({ ...config, plans: updated });
  };

  // Migrate old plans format to new format
  const getPlanDurations = (plan) => {
    if (plan.durations) {
      // Filter out daily and weekly if they exist
      const { daily, weekly, ...validDurations } = plan.durations;
      return {
        monthly: validDurations.monthly || { enabled: false, price: 0 },
        annual: validDurations.annual || { enabled: true, price: 0 },
        biennial: validDurations.biennial || { enabled: false, price: 0 }
      };
    }
    // Migrate from old format (price_annual, price_biannual)
    return {
      monthly: { enabled: false, price: 0 },
      annual: { enabled: true, price: plan.price_annual || 0 },
      biennial: { enabled: plan.price_biannual > 0, price: plan.price_biannual || 0 }
    };
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <CreditCard className="w-5 h-5 text-blue-500" />
              Piani di Abbonamento
            </CardTitle>
            <CardDescription>
              {readOnly ? 'Visualizza i piani, le durate e i prezzi' : 'Configura i piani, le durate disponibili, i prezzi e i limiti'}
            </CardDescription>
          </div>
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={addPlan}
              data-testid="add-plan-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi Piano
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {config?.plans?.map((plan, index) => {
          const durations = getPlanDurations(plan);
          
          return (
            <div key={plan.id || index} className="p-4 rounded-lg border border-border bg-secondary/30 space-y-4" data-testid={`plan-config-${index}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>
                  Piano {index + 1}: {plan.name}
                </h3>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removePlan(index)}
                    data-testid={`remove-plan-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>ID Piano</Label>
                  <Input
                    value={plan.id}
                    onChange={(e) => updatePlan(index, 'id', e.target.value)}
                    data-testid={`plan-id-${index}`}
                    disabled={readOnly}
                    className={readOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={plan.name}
                    onChange={(e) => updatePlan(index, 'name', e.target.value)}
                    data-testid={`plan-name-${index}`}
                    disabled={readOnly}
                    className={readOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Limite Link (vuoto = illimitato)</Label>
                  <Input
                    type="number"
                    placeholder="Illimitato"
                    value={plan.max_links ?? ''}
                    onChange={(e) => updatePlan(index, 'max_links', e.target.value)}
                    data-testid={`plan-max-links-${index}`}
                    disabled={readOnly}
                    className={readOnly ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Limite Ticket (vuoto = illimitato)</Label>
                  <Input
                    type="number"
                    placeholder="Illimitato"
                    value={plan.max_tickets ?? ''}
                    onChange={(e) => updatePlan(index, 'max_tickets', e.target.value)}
                    data-testid={`plan-max-tickets-${index}`}
                    disabled={readOnly}
                    className={readOnly ? 'bg-muted' : ''}
                  />
                </div>
              </div>

              {/* Integrator Options */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  Opzioni Integratore
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className={`flex items-center space-x-2 p-3 rounded-md border ${plan.promotes_to_integrator ? 'border-green-500 bg-green-500/10' : 'border-border bg-muted/30'}`}>
                    <Checkbox
                      id={`promotes-integrator-${index}`}
                      checked={plan.promotes_to_integrator || false}
                      onCheckedChange={(checked) => updatePlan(index, 'promotes_to_integrator', checked)}
                      data-testid={`plan-promotes-integrator-${index}`}
                      disabled={readOnly}
                    />
                    <label
                      htmlFor={`promotes-integrator-${index}`}
                      className={`text-sm font-medium leading-none ${readOnly ? '' : 'cursor-pointer'}`}
                    >
                      <UserPlus className="w-4 h-4 inline mr-1 text-green-500" />
                      Promuovi a Integratore
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label>Limite Utenti (per integratore)</Label>
                    <Input
                      type="number"
                      placeholder="Illimitato"
                      value={plan.max_users ?? ''}
                      onChange={(e) => updatePlan(index, 'max_users', e.target.value)}
                      data-testid={`plan-max-users-${index}`}
                      disabled={readOnly}
                      className={readOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>
                {plan.promotes_to_integrator && (
                  <p className="text-xs text-muted-foreground">
                    Gli utenti che acquistano questo piano diventeranno automaticamente Integratori e potranno gestire i propri utenti.
                  </p>
                )}
              </div>

              {/* Durations */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  Durate e Prezzi
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {Object.entries(DURATION_LABELS).map(([durationId, label]) => {
                    const duration = durations[durationId] || { enabled: false, price: 0 };
                    return (
                      <div 
                        key={durationId} 
                        className={`p-3 rounded-md border ${duration.enabled ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <Checkbox
                            id={`duration-${index}-${durationId}`}
                            checked={duration.enabled}
                            onCheckedChange={(checked) => updateDuration(index, durationId, 'enabled', checked)}
                            data-testid={`plan-duration-${index}-${durationId}-enabled`}
                            disabled={readOnly}
                          />
                          <label
                            htmlFor={`duration-${index}-${durationId}`}
                            className={`text-sm font-medium leading-none ${readOnly ? '' : 'cursor-pointer'}`}
                          >
                            {label}
                          </label>
                        </div>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Prezzo €"
                          value={duration.price || ''}
                          onChange={(e) => updateDuration(index, durationId, 'price', e.target.value)}
                          disabled={!duration.enabled || readOnly}
                          className={`h-8 text-sm ${readOnly ? 'bg-muted' : ''}`}
                          data-testid={`plan-duration-${index}-${durationId}-price`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Available For Roles */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  Disponibile per
                </Label>
                <div className="flex gap-4">
                  {Object.entries(ROLE_LABELS).map(([roleId, label]) => {
                    const availableFor = plan.available_for || ['user', 'integrator'];
                    const isChecked = availableFor.includes(roleId);
                    return (
                      <div 
                        key={roleId} 
                        className={`flex items-center space-x-2 p-3 rounded-md border ${isChecked ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}
                      >
                        <Checkbox
                          id={`available-${index}-${roleId}`}
                          checked={isChecked}
                          onCheckedChange={(checked) => updateAvailableFor(index, roleId, checked)}
                          data-testid={`plan-available-${index}-${roleId}`}
                          disabled={readOnly}
                        />
                        <label
                          htmlFor={`available-${index}-${roleId}`}
                          className={`text-sm font-medium leading-none ${readOnly ? '' : 'cursor-pointer'}`}
                        >
                          {label}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {(plan.available_for || ['user', 'integrator']).length === 0 && (
                  <p className="text-xs text-destructive">Attenzione: nessun ruolo selezionato, il piano non sarà visibile</p>
                )}
              </div>

              {/* Features */}
              <div className="space-y-2">
                <Label>Funzionalità (una per riga)</Label>
                <Textarea
                  rows={3}
                  value={(plan.features || []).join('\n')}
                  onChange={(e) => updatePlan(index, 'features', e.target.value)}
                  data-testid={`plan-features-${index}`}
                  className={`text-sm ${readOnly ? 'bg-muted' : ''}`}
                  disabled={readOnly}
                />
              </div>
            </div>
          );
        })}

        {!readOnly && (
          <Button
            onClick={onSave}
            disabled={saving}
            data-testid="save-subscription-config"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Configurazione Abbonamenti'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
